import { CountryIso3166 } from "@tfg/db/browser"
import {
  CountrySelectorCustomRender,
  FilterBuilderProps,
  FilterOptions,
} from "@tfg/ui"
import _ from "lodash"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { removeUndefined } from "@tfg/types/schemas/utils"

import { useHeaderControlsContext } from "~/components/contexts/useHeaderControlsContext"
import {
  ProductFilterAndSearchRequestDto,
  useFindProductBrandValues,
  useFindProductCollectionValues,
  useFindProductSupplierContactValues,
  useFindProductTypeValues,
} from "~/gen/api"
import { COUNTRIES } from "~/utils/countries"

const EMPTY_FILTERS = {} as ProductFilterAndSearchRequestDto
const EMPTY_EXTRA_FILTERS: FilterBuilderProps<any>["options"] = []

export const useBuildProductFilters = <
  T extends ProductFilterAndSearchRequestDto,
>(
  extraFilters: FilterBuilderProps<T>["options"] = EMPTY_EXTRA_FILTERS,
  defaultFilters: T = EMPTY_FILTERS as T
) => {
  const { filters, setFilters } = useHeaderControlsContext()

  const { t } = useTranslation("products")
  const { data: productTypeValues } = useFindProductTypeValues(
    removeUndefined({ ...filters, type: undefined })
  )
  const { data: productCollectionValues } = useFindProductCollectionValues(
    removeUndefined({ ...filters, collection: undefined })
  )
  const { data: productBrandValues } = useFindProductBrandValues(
    removeUndefined({ ...filters, brand: undefined })
  )
  const { data: supplierContactValues } = useFindProductSupplierContactValues(
    removeUndefined({
      ...filters,
      organizationId: undefined,
      country: undefined,
    })
  )

  const organizations = _.uniqBy(
    supplierContactValues?.data.map((supplier) => supplier.organization),
    "id"
  )

  const countries = _.uniq(
    supplierContactValues?.data.map((supplier) => supplier.organization.country)
  )

  const options: FilterOptions<T>[] = useMemo(() => {
    const baseOptions: FilterOptions<T>[] = [
      {
        label: t("lists.placeholders.type"),
        key: "type",
        type: "multiSelect",
        options:
          productTypeValues?.data.map((type) => ({
            label: type,
            value: type,
          })) ?? [],
      },
      {
        label: t("lists.placeholders.collection"),
        key: "collection",
        type: "multiSelect",
        options:
          productCollectionValues?.data.map((collection) => ({
            label: collection,
            value: collection,
          })) ?? [],
      },
      {
        label: t("lists.placeholders.companies"),
        key: "organizationId",
        options: organizations.map((organization) => ({
          label: organization.name,
          value: organization.id,
        })),
        type: "multiSelect",
      },
      {
        label: t("lists.placeholders.country"),
        key: "country",
        options: Object.entries(COUNTRIES)
          .map(([_, value]) => ({
            label: value.countryName,
            value: value.code,
          }))
          .filter((country) =>
            countries.includes(country.value as CountryIso3166)
          ),
        customRender: CountrySelectorCustomRender(COUNTRIES),
        type: "multiSelect",
      },
    ]

    if (productBrandValues?.data && productBrandValues.data.length > 0) {
      baseOptions.push({
        label: t("lists.placeholders.brand"),
        key: "brand",
        options: productBrandValues.data.map((brand) => ({
          label: brand,
          value: brand,
        })),
        type: "multiSelect",
      })
    }

    return baseOptions
  }, [
    productTypeValues,
    productCollectionValues,
    productBrandValues,
    organizations,
    countries,
    t,
  ])

  const allOptions = useMemo(
    () => [...options, ...extraFilters],
    [options, extraFilters]
  )

  const filtersValue = useMemo(
    () => ({ ...filters, ...defaultFilters }),
    [filters, defaultFilters]
  )

  return {
    filtersValue,
    options: allOptions,
    searchPlaceholder: t("filters.search.placeholder"),
    setFiltersValue: setFilters,
    title: t("filters.title"),
  }
}
